import { useState, useEffect } from 'react';
import { userService } from 'services/api';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import styles from './Users.module.css';
import UserFormModal from './UserFormModal';
import Paginacion from 'components/Paginacion';
import { EMAIL_REGEX } from 'utils/email';

const emptyForm = { name: '', last_name: '', email: '', phone: '', password: '', role: 'user' };

const LIMIT = 50;

// Límites/formatos — mismos que valida el backend (usersController.js).
const FIELD_MAX_LENGTH = { name: 150, last_name: 150 };
const PHONE_REGEX = /^\d{7,10}$/;
const PASSWORD_MIN_LENGTH = 8;

// Trim antes de exigir "no vacío" — un valor de solo espacios pasaba el
// `required` nativo del input (mismo hallazgo que en Property, ver #159).
function validarCampos(form, isEditing) {
  if (!form.name.trim())  return 'El nombre es requerido';
  if (!form.email.trim()) return 'El email es requerido';
  for (const campo of ['name', 'last_name']) {
    if (form[campo].length > FIELD_MAX_LENGTH[campo]) {
      return `El campo "${campo}" no puede tener más de ${FIELD_MAX_LENGTH[campo]} caracteres`;
    }
  }
  if (!EMAIL_REGEX.test(form.email.trim())) return 'El email no es válido';
  if (form.phone.trim() && !PHONE_REGEX.test(form.phone.trim())) {
    return 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57';
  }
  // Contraseña obligatoria al crear; al editar solo si se está cambiando.
  if ((!isEditing || form.password) && form.password.length < PASSWORD_MIN_LENGTH) {
    return `La contraseña debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`;
  }
  if ((!isEditing || form.password) && !form.password.trim()) return 'La contraseña no puede ser solo espacios';
  return null;
}

export default function Users() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const [delError,   setDelError]   = useState('');
  const [formError,  setFormError]  = useState('');
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);

  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };

  const load = (p = page, s = search) => {
    setLoading(true);
    userService.getAll({ page: p, limit: LIMIT, search: s })
      .then(r => {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
        setPage(r.data.page || 1);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    setFormError('');
    const campoError = validarCampos(form, !!editing);
    if (campoError) return setFormError(campoError);
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password; // no enviar password vacío al editar
      if (editing) await userService.update(editing.id, payload);
      else await userService.create(payload);
      setShow(false); setEditing(null); setForm(emptyForm);
      setAlert({ type: 'success', message: editing ? 'Usuario actualizado' : 'Usuario creado' });
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'No se pudo guardar. Inténtalo de nuevo.');
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    setDelError('');
    try {
      await userService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Usuario eliminado' });
      setConfirmDel(null);
      load();
    }
    catch (err) { setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.'); }
    finally { setDeleting(false); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || '', last_name: u.last_name || '', email: u.email || '', phone: u.phone || '', password: '', role: u.role || 'user' });
    setShow(true);
  };
  const close = () => { setShow(false); setEditing(null); setForm(emptyForm); setFormError(''); };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const roleBadge = (role) => {
    const map = { admin: 'badge-primary', user: 'badge-secondary', superadmin: 'badge-warning' };
    return map[role] || 'badge-secondary';
  };

  return (
    <div>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1><i className={`fas fa-users ${styles.iconoCabecera}`} />Usuarios</h1>
          <p>Gestión de usuarios del sistema</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setForm(emptyForm); setShow(true); }}>
          <i className="fas fa-plus" /> Nuevo usuario
        </button>
      </div>

      {alert && <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>{alert.message}</div>}

      <div className="card">
        <div className={`card-header ${styles.encabezado}`}>
          <h2>Lista de usuarios</h2><span className="badge badge-primary">{total}</span>
          <div className={styles.buscadorCaja}>
            <i className={`fas fa-search ${styles.iconoBuscar}`} />
            <input
              type="text"
              className={`form-control ${styles.buscador}`}
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        {loading ? <div className="loading"><div className="spinner" /></div> :
          items.length === 0 ? (
            <div className="empty-state"><i className="fas fa-users" /><p>{search ? `Sin resultados para "${search}"` : 'Sin usuarios registrados'}</p></div>
          ) : (
            <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>ID</th><th>Nombre</th><th>Email</th><th>Teléfono</th><th>Rol</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                  {items.map(u => (
                    <tr key={u.id}>
                      <td>#{u.id}</td>
                      <td><strong>{u.name} {u.last_name}</strong></td>
                      <td>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td><span className={`badge ${roleBadge(u.role)}`}>{u.role || 'user'}</span></td>
                      <td><div className="actions">
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={() => openEdit(u)}><i className="fas fa-pencil" /></button>
                        <button className="btn btn-sm btn-danger btn-icon" onClick={() => setConfirmDel(u)}><i className="fas fa-trash" /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
      </div>
      {!loading && <Paginacion pagina={page} total={total} porPagina={LIMIT} onCambiar={irAPagina} />}

      {show && (
        <UserFormModal editing={editing} form={form} set={set} save={save} close={close} error={formError} />
      )}

      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar usuario" onClose={() => { setConfirmDel(null); setDelError(''); }} onConfirm={del} deleting={deleting} error={delError}>
          <div className={styles.confirmaTitulo}>
            ¿Eliminar <span className={styles.confirmaNombre}>{confirmDel.name} {confirmDel.last_name || ''}</span>?
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}